import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { IonButton } from '@ionic/react';
import './SignaturePad.css';

export interface SignaturePadHandle {
  clear: () => void;
  isEmpty: () => boolean;
  /** PNG data URL cropped to the ink, or null when nothing was drawn. */
  toDataUrl: () => string | null;
}

/** Finger, stylus or mouse signature box. No library: a canvas with pointer events. */
const SignaturePad = forwardRef<SignaturePadHandle>((_props, ref) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const inked = useRef(false);

  const setup = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    const d = window.devicePixelRatio || 1;
    c.width = Math.round(r.width * d);
    c.height = Math.round(r.height * d);
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.scale(d, d);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0b1f44';
    inked.current = false;
  }, []);

  useEffect(() => {
    setup();
  }, [setup]);

  const point = (e: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const r = e.currentTarget.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };

  const down = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = e.currentTarget.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    const [x, y] = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
    inked.current = true;
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = e.currentTarget.getContext('2d');
    if (!ctx) return;
    const [x, y] = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const up = () => {
    drawing.current = false;
  };

  useImperativeHandle(ref, () => ({
    clear: setup,
    isEmpty: () => !inked.current,
    toDataUrl: () => {
      const c = canvasRef.current;
      const ctx = c?.getContext('2d');
      if (!c || !ctx || !inked.current) return null;
      const { width: w, height: h } = c;
      const data = ctx.getImageData(0, 0, w, h).data;
      let x0 = w;
      let y0 = h;
      let x1 = 0;
      let y1 = 0;
      for (let y = 0; y < h; y += 2) {
        for (let x = 0; x < w; x += 2) {
          if (data[(y * w + x) * 4 + 3] > 0) {
            x0 = Math.min(x0, x);
            y0 = Math.min(y0, y);
            x1 = Math.max(x1, x);
            y1 = Math.max(y1, y);
          }
        }
      }
      if (x1 <= x0 || y1 <= y0) return null;
      const m = 10;
      x0 = Math.max(0, x0 - m);
      y0 = Math.max(0, y0 - m);
      x1 = Math.min(w, x1 + m);
      y1 = Math.min(h, y1 + m);
      const sw = x1 - x0;
      const sh = y1 - y0;
      const out = document.createElement('canvas');
      out.width = Math.min(600, sw);
      out.height = Math.round((sh * out.width) / sw);
      out.getContext('2d')?.drawImage(c, x0, y0, sw, sh, 0, 0, out.width, out.height);
      return out.toDataURL('image/png');
    },
  }));

  return (
    <div className="signature-pad">
      <div className="signature-pad-head">
        <span>Signature</span>
        <IonButton size="small" fill="outline" onClick={setup}>
          Clear
        </IonButton>
      </div>
      <canvas
        ref={canvasRef}
        className="signature-pad-canvas"
        role="img"
        aria-label="Signature pad. Sign with a finger, stylus or mouse."
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerLeave={up}
        onPointerCancel={up}
      />
      <div className="signature-pad-hint">Sign with a finger, stylus or mouse.</div>
    </div>
  );
});

SignaturePad.displayName = 'SignaturePad';
export default SignaturePad;
